import { CheckCircle2, XCircle } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';

import {
  useGetPaymentRunReviewDetail,
  type GetPaymentRunReviewParams,
  type PaymentRunReviewDetail,
  type PaymentRunReviewRestoreReasonCode,
} from '~entities/payment/api/reporting-payment-run-review';
import { useRestorePaymentRunReviewMutation } from '~entities/payment/api/restore-payment-run-review';
import { Button } from '~shared/shadcn/ui/button';
import { formatMutationErrorMessage } from '~shared/utils/mutation-error-message';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '~shared/shadcn/ui/sheet';
import { cn } from '~shared/shadcn/lib/utils';

function formatMoney(amount: number, symbol: string): string {
  return `${symbol}${Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h3 className='mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
      {children}
    </h3>
  );
}

function CardBrandMark({ brand }: { brand: string }) {
  const b = brand.toLowerCase();
  if (b === 'visa') {
    return (
      <div className='flex size-10 shrink-0 items-center justify-center rounded bg-[#1A1F71] text-[10px] font-bold tracking-tight text-white'>
        VISA
      </div>
    );
  }
  if (b === 'mastercard') {
    return (
      <div className='flex size-10 shrink-0 items-center justify-center rounded bg-neutral-900 text-[8px] font-bold text-white'>
        MC
      </div>
    );
  }
  return (
    <div className='flex size-10 shrink-0 items-center justify-center rounded bg-neutral-200 text-xs font-semibold text-neutral-700'>
      {brand.slice(0, 2).toUpperCase()}
    </div>
  );
}

function DetailBody({
  detail,
  symbol,
  rowAmounts,
}: {
  detail: PaymentRunReviewDetail;
  symbol: string;
  rowAmounts: {
    baseAmount: number;
    discount: number;
    transactionFee: number;
    finalAmount: number;
  };
}) {
  const { removalReason, paymentMethod, chargeHistory } = detail;
  const subtitle = [detail.studentName, detail.classTitle]
    .filter(Boolean)
    .join(' · ');
  const expStr =
    paymentMethod &&
    paymentMethod.expMonth > 0 &&
    paymentMethod.expYear > 0
      ? `${String(paymentMethod.expMonth).padStart(2, '0')}/${paymentMethod.expYear}`
      : null;

  const visibleHistory = chargeHistory.filter(
    (h) => h.status === 'paid' || h.status === 'failed',
  );

  return (
    <>
      <SheetHeader className='space-y-1 border-b border-border pb-4 pr-8 text-left'>
        <SheetTitle className='min-w-0 break-words text-xl font-bold tracking-tight text-foreground sm:text-2xl'>
          {detail.parentName}
        </SheetTitle>
        <p className='min-w-0 break-words text-sm font-normal text-muted-foreground'>
          {subtitle}
        </p>
      </SheetHeader>

      <div className='space-y-8 pt-6'>
        <section>
          <SectionLabel>Charge breakdown</SectionLabel>
          <div className='rounded-lg border border-neutral-200 bg-neutral-50/80 p-4'>
            <div className='space-y-2.5 text-sm'>
              <div className='flex justify-between gap-4'>
                <span className='text-muted-foreground'>Base Tuition</span>
                <span className='shrink-0 font-medium tabular-nums text-foreground'>
                  {formatMoney(rowAmounts.baseAmount, symbol)}
                </span>
              </div>
              <div className='flex justify-between gap-4'>
                <span className='text-muted-foreground'>Discount</span>
                <span className='shrink-0 font-medium tabular-nums text-red-600'>
                  ({formatMoney(rowAmounts.discount, symbol)})
                </span>
              </div>
              <div className='flex justify-between gap-4'>
                <span className='text-muted-foreground'>Transaction Fee</span>
                <span className='shrink-0 font-medium tabular-nums text-foreground'>
                  {formatMoney(rowAmounts.transactionFee, symbol)}
                </span>
              </div>
              <div className='flex justify-between gap-4 border-t border-neutral-200 pt-3'>
                <span className='font-semibold text-foreground'>
                  Final Amount
                </span>
                <span className='shrink-0 text-base font-bold tabular-nums text-foreground'>
                  {formatMoney(rowAmounts.finalAmount, symbol)}
                </span>
              </div>
            </div>
          </div>
        </section>

        {removalReason && (
          <section>
            <SectionLabel>Removal reason</SectionLabel>
            <div className='flex gap-3 rounded-lg border border-red-200 bg-red-50/90 p-4'>
              <div className='flex size-9 shrink-0 items-center justify-center rounded-full border border-red-200 bg-white'>
                <XCircle
                  className='size-5 text-red-600'
                  strokeWidth={2.25}
                  aria-hidden
                />
              </div>
              <p className='text-sm font-medium leading-relaxed text-red-800'>
                {removalReason.message}
              </p>
            </div>
          </section>
        )}

        <section>
          <SectionLabel>Payment method</SectionLabel>
          {paymentMethod ? (
            <div className='flex items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50/80 p-4'>
              <CardBrandMark brand={paymentMethod.brand} />
              <div className='min-w-0'>
                <p className='text-sm font-semibold text-foreground'>
                  {paymentMethod.brand} ····{paymentMethod.last4}
                </p>
                {expStr && (
                  <p className='mt-0.5 text-xs text-muted-foreground'>
                    Expires {expStr}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className='rounded-lg border border-neutral-200 bg-neutral-50/80 p-4'>
              <p className='text-sm text-muted-foreground'>No card on file</p>
            </div>
          )}
        </section>

        <section>
          <SectionLabel>Charge history</SectionLabel>
          {visibleHistory.length === 0 ? (
            <p className='text-sm text-muted-foreground'>No prior charges</p>
          ) : (
            <ul className='divide-y divide-neutral-200 border-t border-neutral-200'>
              {visibleHistory.map((h, i) => (
                <li
                  key={`${h.date}-${i}`}
                  className='flex items-start gap-3 py-3.5 text-sm'
                >
                  {h.status === 'paid' ? (
                    <CheckCircle2
                      className='mt-0.5 size-5 shrink-0 text-emerald-600'
                      aria-hidden
                    />
                  ) : (
                    <XCircle
                      className='mt-0.5 size-5 shrink-0 text-red-600'
                      strokeWidth={2}
                      aria-hidden
                    />
                  )}
                  <div className='min-w-0 flex-1'>
                    <div className='flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1'>
                      <span className='min-w-0 break-words text-muted-foreground'>
                        {h.date}
                      </span>
                      <span className='shrink-0 font-semibold tabular-nums text-foreground'>
                        {formatMoney(h.amount, symbol)}
                      </span>
                    </div>
                    <span
                      className={cn(
                        'mt-0.5 block font-semibold',
                        h.status === 'paid' && 'text-emerald-600',
                        h.status === 'failed' && 'text-red-600',
                      )}
                    >
                      {h.status === 'paid' ? 'Paid' : 'Failed'}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}

const RESTORE_REASONS: Array<{
  value: PaymentRunReviewRestoreReasonCode;
  label: string;
}> = [
  {
    value: 'catch_up_missed_payment',
    label: 'Catch-up charge for a missed payment',
  },
  {
    value: 're_enrolled_after_drop',
    label: 'Student was re-enrolled after being dropped',
  },
  {
    value: 'intentional_separate_charge',
    label: 'This is intentionally a separate charge',
  },
  { value: 'studio_owner_override', label: 'Studio owner override' },
  { value: 'other', label: 'Other' },
];

export function PaymentRunDetailSheet({
  open,
  onOpenChange,
  invoiceId,
  rowAmounts,
  startDate,
  endDate,
  currencySymbol,
  reportQueryParams,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string | null;
  rowAmounts: {
    baseAmount: number;
    discount: number;
    transactionFee: number;
    finalAmount: number;
  } | null;
  startDate: string;
  endDate: string;
  currencySymbol: string;
  reportQueryParams: GetPaymentRunReviewParams;
}) {
  const [restoreReason, setRestoreReason] = useState<
    PaymentRunReviewRestoreReasonCode | ''
  >('');
  const { data: detail, isLoading, isError } = useGetPaymentRunReviewDetail(
    { invoiceId, startDate, endDate },
    { enabled: open && !!invoiceId && !!startDate && !!endDate },
  );

  const restoreMutation = useRestorePaymentRunReviewMutation({
    reportParams: reportQueryParams,
    detailParams: { invoiceId, startDate, endDate },
  });

  const handleLeaveRemoved = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleRestore = useCallback(() => {
    if (!restoreReason || !invoiceId) return;
    restoreMutation.mutate(
      {
        invoiceId,
        reasonCode: restoreReason,
        startDate,
        endDate,
      },
      {
        onSuccess: () => {
          setRestoreReason('');
          onOpenChange(false);
        },
      },
    );
  }, [restoreMutation, restoreReason, invoiceId, startDate, endDate, onOpenChange]);

  const restoreError = restoreMutation.isError
    ? formatMutationErrorMessage(restoreMutation.error, 'Restore failed')
    : '';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side='right'
        className='flex h-full max-h-dvh w-full max-w-full flex-col gap-0 overflow-hidden border-l bg-background p-0 shadow-xl sm:max-w-[440px]'
      >
        {/* min-h-0 so flex child can shrink; otherwise long charge history expands
            the middle and pushes the restore footer below the viewport */}
        <div className='min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4 pt-14 sm:px-6 sm:pt-12'>
          {isLoading ? (
            <div className='flex h-40 items-center justify-center'>
              <div className='size-8 animate-spin rounded-full border-2 border-muted border-t-amber-500' />
            </div>
          ) : isError ? (
            <p className='text-sm text-destructive'>
              Could not load charge details.
            </p>
          ) : detail && rowAmounts ? (
            <DetailBody
              detail={detail}
              symbol={currencySymbol}
              rowAmounts={rowAmounts}
            />
          ) : null}
        </div>

        {!isLoading && detail && !detail.canRestore ? (
          <div className='shrink-0 border-t border-border bg-muted/30 px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6'>
            <p className='text-sm text-muted-foreground'>
              {detail.displayStatus.toLowerCase() === 'superseded' ? (
                <>
                  This charge was{' '}
                  <span className='font-medium text-foreground'>superseded</span>{' '}
                  when another invoice was chosen for the same billing period.
                  It will not run automatically.
                </>
              ) : (
                <>
                  <span className='font-medium text-foreground'>
                    Restore to run
                  </span>{' '}
                  (reason + buttons) appears only for rows marked{' '}
                  <span className='font-medium text-foreground'>Duplicate</span>{' '}
                  or <span className='font-medium text-foreground'>Dropped</span>
                  . This charge is included in the payment run as shown above.
                </>
              )}
            </p>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='mt-3 w-full sm:w-auto'
              onClick={() => { onOpenChange(false); }}
            >
              Close
            </Button>
          </div>
        ) : null}

        {!isLoading && detail?.canRestore ? (
          <div className='shrink-0 border-t border-border bg-background px-4 py-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:px-6'>
            <SectionLabel>Restore this charge</SectionLabel>
            <p className='mb-4 text-sm text-muted-foreground'>
              If this charge is intentional, you can add it back to the payment
              run.
            </p>
            {restoreMutation.isError && (
              <p className='mb-3 text-sm text-destructive' role='alert'>
                {restoreError}
              </p>
            )}
            <div className='space-y-2'>
              <Label htmlFor='restore-reason' className='text-sm font-medium'>
                Reason for restoring <span className='text-red-600'>*</span>
              </Label>
              <Select
                value={restoreReason}
                onValueChange={(v) =>
                  { setRestoreReason(v as PaymentRunReviewRestoreReasonCode); }
                }
              >
                <SelectTrigger
                  id='restore-reason'
                  className='h-11 w-full bg-background'
                >
                  <SelectValue placeholder='Select a reason...' />
                </SelectTrigger>
                <SelectContent>
                  {RESTORE_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='mt-6 flex flex-col gap-3 sm:flex-row'>
              <Button
                type='button'
                variant='outline'
                className='h-11 min-w-0 w-full flex-1 border-neutral-300 bg-white font-medium text-neutral-900 shadow-sm hover:bg-neutral-50'
                onClick={handleLeaveRemoved}
                disabled={restoreMutation.isPending}
              >
                Leave Removed
              </Button>
              <Button
                type='button'
                disabled={
                  !invoiceId || !restoreReason || restoreMutation.isPending
                }
                className='h-11 min-w-0 w-full flex-1 bg-amber-500 font-semibold text-white shadow-sm hover:bg-amber-600 disabled:opacity-50'
                onClick={handleRestore}
              >
                {restoreMutation.isPending ? 'Restoring…' : 'Restore to Run'}
              </Button>
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
